import axios from 'axios';
import clone from 'clone';
import { v4 as uuid } from 'uuid';
import moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { Gizmo } from '../../../gizmos';
import {
  IBinderSetDataParams,
  IGizmo,
  IGizmoData,
  IGizmoEvent,
  IRealmUserEvent,
  IUserV2,
  TGetInfoCB,
} from '../../../types';
import { gnov, isListEqual } from '../../../utils';
import { downloadFileS3 } from '../utils';
import CommonBinder from '../../common-binder';
import transformEntity from './transform-entity';

function getAddressDetails(address: any) {
  let street, location, neighborhood, zipcode, municipality, state;

  if (address) {
    const data = address.split('\n');

    if (data.length > 2) {
      try {
        street = data.slice(0, -2).join(' ');
      } catch (e) {
        console.log('getAddress.eeeeeeeeee: ', e);
      }
      if (data[data.length - 2] && data[data.length - 1]) {
        try {
          [location, neighborhood, zipcode] = data[data.length - 2].match(
            /^(.*) (\d+)$/m
          );
        } catch (e) {}
        try {
          [municipality, state] = data[data.length - 1].split(',');
        } catch (e) {}
      }
    } else if (data.length == 2) {
      [municipality, state] = data[1].split(',');
    }
  }

  return {
    address,
    street,
    location,
    neighborhood,
    zipcode,
    municipality,
    state,
  };
}

function getFileFromImageData(
  imageData: ImageData,
  fileName: string
): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;

    const ctx = canvas.getContext('2d');
    ctx?.putImageData(imageData, 0, 0);

    canvas.toBlob(async blob => {
      if (blob) {
        const file = new File([blob], `${fileName}.jpg`, {
          type: 'image/jpeg',
        });
        resolve(file);
      } else {
        reject();
      }
    }, 'image/jpeg');
  });
}

interface IIdOcrFieldBinder {
  init(): Promise<void>;
  // setFiles({ frontIdFile: File }): Promise<void>
}

class IdOcrFieldBinder extends CommonBinder implements IIdOcrFieldBinder {
  /**
   * Object with the front and back files
   */
  private _files: { [key: string]: File } = {};
  private _onlyFront: boolean;
  public files: Subject<{ [key: string]: File }>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this._onlyFront = false;

    this.files = new Subject<{ [key: string]: File }>();
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const config = this._gizmo.getConfig();
      const gizmoData = this._gizmo.getData();

      if (gizmoData && Array.isArray(gizmoData) && apiUrl && user) {
        this._files = {};
        const platformInterface = this._gizmo
          .getFoundation()
          ?.getPlatformInterface();

        for (let i = 0; i < gizmoData.length; i++) {
          const data = gizmoData[i];

          let file: File | undefined;
          if (
            platformInterface &&
            data.id?.attachmentId &&
            data.id?.fileExtension
          ) {
            const result = await platformInterface.fileGet({
              path: '',
              fileFid: data.id.attachmentId,
              fileExtension: data.id.fileExtension,
            });
            file = result?.file;
          }

          if (!file) {
            // file = await this._downloadFileV2({
            //   data,
            //   answerIndex: i.toString(),
            // });
            file = await this._downloadFilesS3({
              data,
              answerIndex: i.toString(),
            });
          }

          if (file) {
            if (i === 0) {
              this._files.front = file;
            } else if (i === 1) {
              this._files.back = file;
            }
          }
        }
      } else if (gizmoData && !Array.isArray(gizmoData) && apiUrl && user) {
        this._files = {};
        const platformInterface = this._gizmo
          .getFoundation()
          ?.getPlatformInterface();

        const data = gizmoData;

        if (data.id?.onlyFront !== undefined) {
          this._onlyFront = data.id.onlyFront;
        }

        let file: File | undefined;
        if (
          platformInterface &&
          data.id?.attachmentId &&
          data.id?.fileExtension
        ) {
          const result = await platformInterface.fileGet({
            path: '',
            fileFid: data.id.attachmentId,
            fileExtension: data.id.fileExtension,
          });
          file = result?.file;
        }

        if (!file) {
          // file = await this._downloadFileV2({
          //   data,
          //   answerIndex: '0',
          // });

          file = await this._downloadFilesS3({
            data,
            answerIndex: '0',
          });
        }

        if (file) {
          this._files.front = file;
        }
      }

      let scannerEnabled = true;
      if (config.ops?.idOcrField?.scannerEnabled?.isEnabled !== undefined) {
        scannerEnabled = config.ops?.idOcrField?.scannerEnabled?.isEnabled;
      }
      if (config.ops?.idOcrField?.scannerEnabled?.flux) {
        const flux = this._gizmo.getFlux();
        const result = await flux?.evaluate({
          script: config.ops?.idOcrField?.scannerEnabled?.flux,
        });
        if (result !== undefined) {
          scannerEnabled = result;
        }
      }

      if (gizmoData === undefined && config.ops?.value?.flux) {
        const flux = this._gizmo.getFlux();

        if (flux) {
          let conditionResult = true;

          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });

            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (conditionResult === true) {
            const result = await flux.evaluate({
              script: config.ops.value.flux,
            });
            let ocrResult: any = undefined;

            if (result && typeof result === 'object' && result.files) {
              if (
                result.data &&
                Array.isArray(result.data) &&
                result.data[0].id
              ) {
                ocrResult = result.data[0].id?.ocrResult;
              }

              for (const key in result.files) {
                if (key === 'front' || key === 'back') {
                  await this.setFile(key, result.files[key], ocrResult);
                }
              }
            }
          }
        }
      }

      this._gizmo.setFeatures({
        singleFeatures: [
          {
            path: 'idOcrField.scannerEnabled',
            value: scannerEnabled,
          },
        ],
      });

      resolve();
    });
  };

  public setFile = (
    type: 'front' | 'back',
    file: File,
    ocrResult?: any
  ): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      this._files[type] = file;

      if (this._files.front && this._files.back) {
        const gizmoData: IGizmoData[] = [];
        const platformInterface = this._gizmo
          .getFoundation()
          ?.getPlatformInterface();

        if (config.ops?.idOcrField?.processingEnabled !== false && !ocrResult) {
          const data = await this._processFiles();

          this._processPersonalIdResponse(data);

          ocrResult = data.personal_id;
          delete ocrResult.result.faceImageBase64;
          delete ocrResult.result.fullDocumentBackImageBase64;
          delete ocrResult.result.fullDocumentFrontImageBase64;
        }

        // Front file
        const file = this._files.front;
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();
        const attachmentId = uuid();

        if (platformInterface && fileExtension) {
          await platformInterface.fileSet({
            path: '',
            fileFid: attachmentId,
            fileName,
            fileExtension,
            file,
          });
        }

        const gDFront: IGizmoData = {
          v: attachmentId,
          id: {
            attachmentId,
            source: 'web',
            ocrResult,
            fileName,
            fileExtension,
            fileSize: file.size,
            fileType: file.type,
            onlyFront: this._onlyFront,
          },
        };

        gizmoData.push(gDFront);
        this._files.front = file;

        // Back file
        const fileBack = this._files.back;
        const fileNameBack = fileBack.name;
        const fileExtensionBack = fileNameBack.split('.').pop();
        const attachmentIdBack = uuid();

        if (platformInterface && fileExtensionBack) {
          await platformInterface.fileSet({
            path: '',
            fileFid: attachmentIdBack,
            fileName: fileNameBack,
            fileExtension: fileExtensionBack,
            file: fileBack,
          });
        }

        const gDBack: IGizmoData = {
          v: attachmentIdBack,
          id: {
            attachmentId: attachmentIdBack,
            source: 'web',
            fileName: fileNameBack,
            fileExtension: fileExtensionBack,
            fileSize: fileBack.size,
            fileType: fileBack.type,
          },
        };

        gizmoData.push(gDBack);
        this._files.back = fileBack;

        await this._gizmo.setData(gizmoData);
      }

      resolve();
    });
  };

  /**
   * Set data to the gizmo
   * @param data gizmo data to be set
   */
  public setData = async ({
    data,
    event,
  }: IBinderSetDataParams): Promise<void> => {
    const rootGizmoConfig = event?.data?.gizmoSetData?.rootGizmoConfig;
    const rootGizmoData = event?.data?.gizmoSetData?.rootGizmoData;
    const destiny = event?.data?.gizmoSetData?.destiny;

    console.log('IdOcrFieldBinder.setData->data: ', data, ', event: ', event);
    console.log(this._gizmo.getConfig().label, ' --- ', this._files);

    let gizmoConfig: IGizmo | undefined;
    let originFid = destiny.originFid;
    if (
      !originFid &&
      destiny.originUid &&
      rootGizmoConfig?.gizmos &&
      rootGizmoConfig.gizmos.length > 0
    ) {
      gizmoConfig = rootGizmoConfig.gizmos.find(
        (gizmo: any) => gizmo.uid === destiny.originUid
      );
    } else if (originFid && rootGizmoConfig?.gizmos) {
      gizmoConfig = rootGizmoConfig.gizmos.find(
        (gizmo: any) => gizmo.fid === originFid
      );
    }

    const getInfoCB: TGetInfoCB = ({ type }) => {
      if (type === 'rootGizmoFid' && rootGizmoConfig) {
        return rootGizmoConfig.fid;
      } else if (type === 'rootGizmoDataFid' && rootGizmoData) {
        return rootGizmoData.fid;
      }

      return undefined;
    };

    // We're creating a Gizmo shell in order for the download file function to use it
    const gizmo: any = {
      getDao: this._gizmo.getDao,
      getCallbacks: () => ({
        getInfoCB,
      }),
      getConfig: () => gizmoConfig,
    };

    if (data && Array.isArray(data)) {
      const prevData = this._gizmo.getData();
      if (prevData && Array.isArray(prevData) && isListEqual(prevData, data)) {
        return;
      }

      for (let i = 0; i < data.length; i++) {
        const d = data[i];

        const file = await downloadFileS3({
          data: d,
          answerIndex: i.toString(),
          context: {
            gizmo,
          },
        });

        if (file) {
          if (i === 0) {
            this.setFile('front', file);
          } else if (i === 1) {
            this.setFile('back', file);
          }
        }
      }

      this.files.next(this._files);
    } else if (data && !Array.isArray(data)) {
      const file = await downloadFileS3({
        data,
        answerIndex: '0',
        context: {
          gizmo,
        },
      });

      if (file) {
        this.setFile('front', file);
        this.files.next(this._files);
      }
    }
  };

  public setOnlyFront = (onlyFront: boolean): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      this._onlyFront = onlyFront;

      if (this._files.front && onlyFront) {
        const gizmoData: IGizmoData[] = [];
        const platformInterface = this._gizmo
          .getFoundation()
          ?.getPlatformInterface();

        const currentData = this._gizmo.getData();
        if (
          currentData &&
          Array.isArray(currentData) &&
          currentData.length > 0
        ) {
          const ocrResult = currentData.find(cD => cD.id?.ocrResult);

          // If we already have an ocrResult, we don't need to process the file again.
          // We're doing this to avoid doing to many requets to the OCR service.
          if (ocrResult) {
            return resolve();
          }
        }

        let ocrResult: any = undefined;

        if (config.ops?.idOcrField?.processingEnabled !== false) {
          const data = await this._processFiles();
          this._processPersonalIdOnlyFrontResponse(data);
          ocrResult = data.personal_id;
          delete ocrResult.result.faceImageBase64;
          delete ocrResult.result.fullDocumentBackImageBase64;
          delete ocrResult.result.fullDocumentFrontImageBase64;
        }

        // Front file
        const file = this._files.front;
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();
        const attachmentId = uuid();

        if (platformInterface && fileExtension) {
          await platformInterface.fileSet({
            path: '',
            fileFid: attachmentId,
            fileName,
            fileExtension,
            file,
          });
        }

        const gDFront: IGizmoData = {
          v: attachmentId,
          id: {
            attachmentId,
            source: 'web',
            ocrResult,
            fileName,
            fileExtension,
            fileSize: file.size,
            fileType: file.type,
            onlyFront: this._onlyFront,
          },
        };

        gizmoData.push(gDFront);

        await this._gizmo.setData(gizmoData);
      }

      resolve();
    });
  };

  public getOnlyFront = (): boolean => {
    return this._onlyFront;
  };

  /**
   * Get object with the front and back files
   * @returns
   */
  public getFiles = (): { [key: string]: File } | undefined => {
    return this._files;
  };

  public setOcrResult = (ocrResult: any): Promise<void> => {
    return new Promise<void>(async resolve => {
      const gizmoConfig = this._gizmo.getConfig();
      const database = this._gizmo.getDao()?.getDatabase();
      const user = this._gizmo.getDao()?.getUser();

      // TODO: add get info from ocrResult code (get name, address, section, etc.)
      // after getting the info we're going to generate the necessary events

      // console.log('IdOcrFieldBinder->setOcrResult', ocrResult);

      if (ocrResult) {
        let dateOfBirth;
        if (ocrResult?.dateOfBirth?.year) {
          const { day, month, year } = ocrResult?.dateOfBirth;
          dateOfBirth = `${day}/${month}/${year}`;
        }

        const address = getAddressDetails(ocrResult?.address?.latin);
        let state: string | undefined = address?.state
          ?.replace(/\./g, '')
          .replace(/\s{2,}/g, ' ')
          .trim();

        // console.log(
        //   '*************s: ',
        //   state,
        //   ' , config: ',
        //   gizmoConfig.ops?.idOcrField
        // );
        if (
          state &&
          gizmoConfig.ops?.idOcrField?.personalIdOptions?.entityTransform
        ) {
          const transformedState = transformEntity(state);
          if (transformedState) {
            state = transformedState;
          }
        }

        let frontImage = ocrResult?.fullDocumentFrontImage?.rawImage;
        const oR = clone(ocrResult);
        delete oR.faceImage;
        delete oR.fullDocumentBackImage;
        delete oR.fullDocumentFrontImage;
        delete oR.signatureImage;

        if (frontImage) {
          frontImage = await getFileFromImageData(frontImage, 'fron_image');
          this.setFile('front', frontImage, oR);
        }
        let backImage = ocrResult?.fullDocumentBackImage?.rawImage;
        if (backImage) {
          backImage = await getFileFromImageData(backImage, 'back_image');
          this.setFile('back', backImage, oR);
        }
        let faceImage = ocrResult?.faceImage?.rawImage;
        if (faceImage) {
          faceImage = await getFileFromImageData(faceImage, 'face_image');
        }
        console.log('aaaaaaaaaaaa: ', address);

        const personData: any = {
          faceImage,
          fullDocumentFrontImage: frontImage,
          fullDocumentBackImage: backImage,
          name: ocrResult?.fullName?.latin,
          lastName: ocrResult?.fathersName?.latin,
          secondLastName: ocrResult?.mothersName?.latin,
          gender: ocrResult?.sex?.latin,
          dateOfBirth,
          address: ocrResult?.address?.latin,
          curp: ocrResult?.personalIdNumber?.latin,
          electoralSection:
            ocrResult?.mrz?.sanitizedOpt1?.slice(0, 4) ||
            ocrResult?.documentNumber?.latin?.slice(0, 4),
          street: address.street,
          neighborhood: address.neighborhood,
          zipcode: address.zipcode,
          municipality: address.municipality
            ?.replace(/\./g, '')
            .replace(/\s{2,}/g, ' ')
            .trim(),
          electorKey: ocrResult?.documentAdditionalNumber?.latin,
          state,
          entity: address.state,
          electoralEntity: '', //electoralState?
          cic: ocrResult?.mrz?.documentNumber,
          ocr: ocrResult?.mrz?.documentNumber.length
            ? ocrResult?.mrz?.sanitizedOpt1
            : ocrResult?.documentNumber?.latin,
          mrz: ocrResult?.mrz?.rawMRZString,
          expiryYear: ocrResult?.dateOfExpiry?.year,
          issueYear: ocrResult?.dateOfIssue?.year,
          registrationYear: ocrResult?.documentOptionalAdditionalNumber?.latin?.slice(
            0,
            4
          ),
          emissionNumber: ocrResult?.documentOptionalAdditionalNumber?.latin?.slice(
            4,
            6
          ),
        };

        console.log('pD: ', personData);

        if (database && user) {
          const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
            type: 'rootGizmoFid',
          });
          const rootGizmoDataFid = this._gizmo.getCallbacks().getInfoCB({
            type: 'rootGizmoDataFid',
          });

          const realmUserEvent: IRealmUserEvent = {
            fid: uuid(),
            category: 'gizmo',
            type: 'idOcr',
            userId: user.id,
            timestamp: Math.floor(Date.now() / 1000),
            timezone: moment.tz.guess(),
            uploaded: false,
            // TODO: add device info, so we can identify same user in multiple devices
            data: {
              username: user.username,
              rootGizmoFid,
              rootGizmoDataFid,
              gizmoFid: this._gizmo.getConfig().fid,
              authFormId: user.credentials?.['auth-form-fid'],
            },
          };
          // userId, username, authFormId, *coreVersion, *domain, rootGizmoFid, rootGizmoDataFid, gizmoFid

          try {
            console.log('setUUUUUUUserevent: ', realmUserEvent);
            await database.set({
              type: 'userEvents',
              value: realmUserEvent,
            });
          } catch (e) {
            console.log('IdOcrFieldBinder.setOcrResult.userEvent.error: ', e);
          }
        }

        // TODO: get the images from the result
        // after getting the images we're going to generate the necessary events
        // and send the images to the platform layer

        if (gizmoConfig.ops?.idOcrField?.personalId) {
          const personalIdConfig: any = gizmoConfig.ops.idOcrField.personalId;

          for (const key in personalIdConfig) {
            const prefix = key.replace(/(GizmoFid|GizmoUid)$/, '');
            const gizmoFid = personalIdConfig[key];

            const value = personData[prefix];

            if (value !== undefined) {
              const event: IGizmoEvent = {
                type: 'gizmo.set-data',
                src: {
                  type: 'gizmo',
                  gizmoFid: gizmoConfig.fid,
                  gizmoUid: gizmoConfig.uid,
                },
                dest: {
                  type: 'gizmo',
                  gizmoFid,
                },
                data: {
                  gizmoSetData: {
                    //! THIS IS JUST AN EXAMPLE
                    //value: 'NAME',
                    value: personData[prefix],
                  },
                },
              };

              this._gizmo.getEvents().next(event);
            }
          }
        }
      }

      resolve();
    });
  };

  public _processFiles = (): Promise<any> => {
    return new Promise<any>(async resolve => {
      const config = this._gizmo.getConfig();

      if (config.ops?.fileField?.serverProcessing?.type === 'personal_id') {
        const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
        const userCredentials: any = this._gizmo.getDao()?.getUser()
          ?.credentials;

        if (apiUrl && userCredentials && this._files.front) {
          try {
            const url = `${apiUrl}/main_api/v1/files/root_gizmo_data/process`;
            const formData = new FormData();
            formData.append('file', this._files.front);
            if (this._files.back) {
              formData.append('file_two', this._files.back);
            }
            formData.append('gizmo', JSON.stringify(config));

            const response = await axios.post(url, formData, {
              headers: {
                ...userCredentials,
                'Content-Type': 'multipart/form-data',
              },
            });

            if (response.status === 200) {
              const { data } = response;

              if (
                config.ops.fileField.serverProcessing.type === 'personal_id'
              ) {
                return resolve(data);
              }
            }
          } catch (e) {
            console.log('IdOcrFieldBinder.setFiles error: ', e);
          }
        }
      }

      resolve(undefined);
    });
  };

  private _processPersonalIdResponse = (data: any): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      if (
        config.ops?.fileField?.serverProcessing?.personalId &&
        data.personal_id
      ) {
        const {
          nameGizmoUid,
          nameGizmoFid,
          lastNameGizmoUid,
          lastNameGizmoFid,
          secondLastNameGizmoUid,
          secondLastNameGizmoFid,

          streetGizmoUid,
          streetGizmoFid,
          zipcodeGizmoUid,
          zipcodeGizmoFid,
        } = config.ops.fileField.serverProcessing.personalId;

        if (nameGizmoUid || nameGizmoFid) {
          const name = gnov(data.personal_id, 'result.fullName');

          if (name) {
            this._sendEvent({
              uid: nameGizmoUid,
              fid: nameGizmoFid,
              value: name,
            });
          }
        }

        if (lastNameGizmoUid || lastNameGizmoFid) {
          const lastName = gnov(
            data.personal_id,
            'result.frontViz.fathersName'
          );

          if (lastName) {
            this._sendEvent({
              uid: lastNameGizmoUid,
              fid: lastNameGizmoFid,
              value: lastName,
            });
          }
        }

        if (secondLastNameGizmoUid || secondLastNameGizmoFid) {
          const secondLastName = gnov(
            data.personal_id,
            'result.frontViz.mothersName'
          );

          if (secondLastName) {
            this._sendEvent({
              uid: secondLastNameGizmoUid,
              fid: secondLastNameGizmoFid,
              value: secondLastName,
            });
          }
        }

        if (
          streetGizmoUid ||
          streetGizmoFid ||
          zipcodeGizmoUid ||
          zipcodeGizmoFid
        ) {
          const address = gnov(data.personal_id, 'result.frontViz.address');

          const { street, zipcode } = getAddressDetails(address);

          if (street && (streetGizmoUid || streetGizmoFid)) {
            this._sendEvent({
              uid: streetGizmoUid,
              fid: streetGizmoFid,
              value: street,
            });
          }

          if (zipcode && (zipcodeGizmoUid || zipcodeGizmoFid)) {
            this._sendEvent({
              uid: zipcodeGizmoUid,
              fid: zipcodeGizmoFid,
              value: zipcode,
            });
          }
        }
      }

      resolve();
    });
  };

  private _processPersonalIdOnlyFrontResponse = (data: any): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      if (
        config.ops?.fileField?.serverProcessing?.personalId &&
        data.personal_id
      ) {
        const {
          nameGizmoUid,
          nameGizmoFid,
          lastNameGizmoUid,
          lastNameGizmoFid,
          secondLastNameGizmoUid,
          secondLastNameGizmoFid,

          streetGizmoUid,
          streetGizmoFid,
          zipcodeGizmoUid,
          zipcodeGizmoFid,
        } = config.ops.fileField.serverProcessing.personalId;

        if (nameGizmoUid || nameGizmoFid) {
          const name = gnov(data.personal_id, 'result.fullName');

          if (name) {
            this._sendEvent({
              uid: nameGizmoUid,
              fid: nameGizmoFid,
              value: name,
            });
          }
        }

        if (lastNameGizmoUid || lastNameGizmoFid) {
          const lastName = gnov(data.personal_id, 'result.viz.fathersName');

          if (lastName) {
            this._sendEvent({
              uid: lastNameGizmoUid,
              fid: lastNameGizmoFid,
              value: lastName,
            });
          }
        }

        if (secondLastNameGizmoUid || secondLastNameGizmoFid) {
          const secondLastName = gnov(
            data.personal_id,
            'result.viz.mothersName'
          );

          if (secondLastName) {
            this._sendEvent({
              uid: secondLastNameGizmoUid,
              fid: secondLastNameGizmoFid,
              value: secondLastName,
            });
          }
        }

        if (
          streetGizmoUid ||
          streetGizmoFid ||
          zipcodeGizmoUid ||
          zipcodeGizmoFid
        ) {
          const address = gnov(data.personal_id, 'result.viz.address');

          const { street, zipcode } = getAddressDetails(address);

          if (street && (streetGizmoUid || streetGizmoFid)) {
            this._sendEvent({
              uid: streetGizmoUid,
              fid: streetGizmoFid,
              value: street,
            });
          }

          if (zipcode && (zipcodeGizmoUid || zipcodeGizmoFid)) {
            this._sendEvent({
              uid: zipcodeGizmoUid,
              fid: zipcodeGizmoFid,
              value: zipcode,
            });
          }
        }
      }
      resolve();
    });
  };

  private _sendEvent = ({ fid, uid, value }: ISendEvent): void => {
    const config = this._gizmo.getConfig();
    const event: IGizmoEvent = {
      type: 'gizmo.set-data',
      src: {
        type: 'gizmo',
        gizmoUid: config.uid,
      },
    };

    if (fid) {
      event.dest = {
        type: 'gizmo',
        gizmoFid: fid,
      };
    } else if (uid) {
      event.dest = {
        type: 'gizmo',
        gizmoUid: uid,
      };
    }

    if (value) {
      event.data = {
        gizmoSetData: {
          value,
        },
      };
    }

    this._gizmo.getEvents().next(event);
  };

  private _downloadFilesS3 = async ({
    data,
    fid,
    formId,
    filledFormId,
    answerIndex,
  }: IDownloadFileV2): Promise<File | undefined> => {
    return await downloadFileS3({
      data,
      fid,
      formId,
      filledFormId,
      answerIndex,
      context: {
        gizmo: this._gizmo,
      },
    });
  };

  /**
   * Downloads file from the server from the v2 api
   *
   * @param {IGizmoData} data gizmo data
   * @returns
   */
  private _downloadFileV2 = async ({
    data,
    fid,
    formId,
    filledFormId,
    answerIndex,
  }: IDownloadFileV2): Promise<File | undefined> => {
    return new Promise<File | undefined>(async resolve => {
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const callbacks = this._gizmo.getCallbacks();
      const config: IGizmo = this._gizmo.getConfig();

      if (!formId) {
        formId = callbacks.getInfoCB({ type: 'rootGizmoFid' });
      }
      if (!filledFormId) {
        filledFormId = callbacks.getInfoCB({ type: 'rootGizmoDataFid' });
      }
      const fId = fid || config.fid;
      const attachmentId = data.id?.attachmentId || data.fd?.attachmentId;
      const extension = data.id?.fileExtension || data.fd?.fileExtension;

      if (apiUrl && user) {
        const credentials: any = user.credentials;
        let url = `${apiUrl}/v3/forms/${formId}/filled_forms_attachment?`;
        url += `attachmentId=${attachmentId}&formId=${formId}&filledFormId=${filledFormId}&`;
        url += `fId=${fId}&answerIndex=${answerIndex}&extension=${extension}`;

        try {
          const response = await axios({
            method: 'GET',
            url,
            responseType: 'blob',
            headers: {
              ...credentials,
            },
          });

          if (response.status === 200 || response.status === 201) {
            let type = '';
            if (extension === 'pdf') {
              type = 'application/pdf';
            } else if (extension === 'jpg' || extension === 'jpeg') {
              type = 'image/jpeg';
            }

            const file = new File(
              [response.data],
              `${attachmentId}.${extension}`,
              { type }
            );

            return resolve(file);
          }
        } catch (e) {
          console.log('*******************e: ', e);
        }
      }

      resolve(undefined);
    });
  };

  /**
   * Function used to set the files from the UI layer
   *
   * @param {File} frontIdFile front image of the id
   * @param {File} backIdFile back image of the id
   * @returns
   */
  // public setFiles = ({ frontIdFile, backIdFile }: ISetFiles): Promise<void> => {
  //   return new Promise<void>(async resolve => {
  //     const config = this._gizmo.getConfig();

  //     console.log('---------->>front: ', frontIdFile, ', back: ', backIdFile);

  //     if (config.ops?.fileField?.serverProcessing?.type === 'national_id') {
  //       const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
  //       const userCredentials: any = this._gizmo.getDao()?.getUser()
  //         ?.credentials;

  //       if (apiUrl && userCredentials && frontIdFile && backIdFile) {
  //         try {
  //           const url = `${apiUrl}/main_api/v1/files/root_gizmo_data/process`;
  //           const formData = new FormData();
  //           formData.append('file', frontIdFile);
  //           formData.append('file_two', backIdFile);
  //           formData.append('gizmo', JSON.stringify(config));

  //           const response = await axios.post(url, formData, {
  //             headers: {
  //               ...userCredentials,
  //               'Content-Type': 'multipart/form-data',
  //             },
  //           });

  //           if (response.status === 200) {
  //             const { data } = response;

  //             console.log('ddddddddddddddata: ', data);
  //           }
  //         } catch (e) {
  //           console.log('IdOcrFieldBinder.setFiles error: ', e);
  //         }
  //       }
  //     }

  //     resolve();
  //   });
  // };
}

interface ISetFiles {
  frontIdFile?: File;
  backIdFile?: File;
}

interface ISendEvent {
  uid?: string;
  fid?: string;
  value?: string;
}

interface IDownloadFileV2 {
  data: IGizmoData;
  fid?: string;
  formId?: string;
  filledFormId?: string;
  answerIndex: string;
}

export default IdOcrFieldBinder;
